<script setup lang="ts">
import { useEmitter } from "@/composition/useEmitter";
import { useIMEComposition } from "@/composition/useIMEComposition";
import { useAppStore } from "@/store/app";
import {
  handleRemoveAllCustomObjectFields,
  isOnlyCustomObjectField,
  isPrimaryField,
} from "@/util/customObjectUtils";
import {
  buildClonedCustomFieldPayload,
  fixSafariCursor,
  focusLabelAfterActiveChange,
  getCaretOffsetWithin,
  handleLabelTabNavigation,
  handleShortLabelInput as handleShortLabelInputUtil,
  isBuilderElementActive,
  scrollContainerBySlideHeight,
  setCaretOffsetWithin,
} from "@/util/methods";
import {
  Copy05Icon,
  PlusIcon,
  Settings02Icon,
  Trash01Icon,
} from "@gohighlevel/ghl-icons/24/outline";
import { UIButton } from "@gohighlevel/ghl-ui";
import { v4 as uuidv4 } from "uuid";
import { computed, nextTick, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { CONSTANTS, JUMP_TO_FIELDS } from "../../util/constants";
import AlertModal from "./AlertModal.vue";
import CheckboxField from "./CheckboxField.vue";
import RatingField from "./Fields/RatingElement.vue";
import FileUploadField from "./FileUploadField.vue";
import LargeTextField from "./LargeTextField.vue";
import MonetaryField from "./MonetaryField.vue";
import NumericalField from "./NumericalField.vue";
import RadioField from "./RadioField.vue";
import RemovePrimaryFieldModal from "./RemovePrimaryFieldModal.vue";
import SignatureField from "./SignatureField.vue";
import SingleOptionsField from "./SingleOptionsField.vue";
import TextField from "./TextField.vue";
import TextboxListField from "./TextboxListField.vue";
const { t } = useI18n();

const store = useAppStore();
const emitter = useEmitter();
const { handleCompositionStart, handleCompositionEnd, withIMECheck, resetKey } =
  useIMEComposition();

const currentLayout = computed(() => {
  return store.deviceMode === "mobile"
    ? store.builderConfig.mobileLayout || store.builderConfig.layout
    : store.builderConfig.layout;
});

const props = defineProps({
  element: {
    type: Object,
    default() {
      return {};
    },
  },
  fieldStyle: Object,
  index: Number,
  slideIndex: {
    type: Number,
    default: undefined,
  },
  currentElement: Object,
  isForm: {
    type: Boolean,
    default: true,
  },
  formLabelVisible: {
    type: Boolean,
    default: true,
  },
  slide: {
    type: Object,
    default: () => {
      return {};
    },
  },
});

const showDeleteModal = ref(false);
const showRemovePrimaryFieldModal = ref(false);
const deleteIndex = ref(0);
const deleteSlideIndex = ref<number | undefined>(undefined);
const primaryFieldInfo = ref({ fieldName: "", objectName: "", objectSingular: "" });
const emit = defineEmits([
  "update:label",
  "update:placeholder",
  "update:shortLabel",
  "update:lowestRating",
  "update:highestRating",
]);
const editableLabel = ref<HTMLElement | null>(null);
const inputElement = ref<HTMLInputElement | null>(null);
const localLabel = ref(props.element.label || "");
const inputClicked = ref(false);
const shortLabelElement = ref<HTMLElement | null>(null);

// Handler method to call the imported fixSafariCursor function
const handleSafariCursor = (e: Event) => {
  const isKeyDown = e.type === "keydown";
  fixSafariCursor(e, isKeyDown);
};

watch(
  () => props.element.label,
  (newLabel) => {
    localLabel.value = newLabel || "";
  }
);

const localPlaceholder = ref(props.element.placeholder || "");
const localShortLabel = ref(props.element.shortLabel || "");
watch(
  () => props.element.placeholder,
  (newVal) => {
    localPlaceholder.value = newVal || "";
  }
);
watch(
  () => props.element.shortLabel,
  (newVal) => {
    localShortLabel.value = newVal || "";
  }
);
const updateLocalShortLabel = () => {
  onUpdateShortLabel(localShortLabel.value);
};
const updateLocalPlaceholder = () => {
  onUpdatePlaceholder(localPlaceholder.value);
};
const onLabelBlur = () => {
  store.addToHistory();
  inputClicked.value = false;
};
const onLabelInput = () => {
  emit("update:label", localLabel.value);
};
const onUpdatePlaceholder = (newPlaceholder: string) => {
  emit("update:placeholder", newPlaceholder);
  store.addToHistory();
};
const onUpdateShortLabel = (newShortLabel: string) => {
  emit("update:shortLabel", newShortLabel);
  store.addToHistory();
};

const onUpdateLowestRating = (value: string) => {
  emit("update:lowestRating", value);
  store.addToHistory();
};

const onUpdateHighestRating = (value: string) => {
  emit("update:highestRating", value);
  store.addToHistory();
};
const handleFocus = (event: FocusEvent) => {
  const target = event.target as HTMLElement;
  const range = document.createRange();
  range.selectNodeContents(target);
  const selection = window.getSelection();
  if (selection) {
    selection.removeAllRanges();
    selection.addRange(range);
  }
};

// Auto-focus and select label text when this element becomes active
const isActive = computed(() =>
  isBuilderElementActive(store, {
    elementIndex: props.index ?? 0,
    slideIndex: props.slideIndex,
    elementId: (props.element as any)?.uuid,
    slideId: (props.slide as any)?.id,
  })
);

watch(
  () => isActive.value,
  async (active) => {
    if (active) {
      await nextTick();
      focusLabelAfterActiveChange();
      // Prevent subsequent first-click from reselecting all text
      inputClicked.value = true;
    }
  }
);

const successCallbackDeleteUpdate = () => {
  store.builderConfig.conditionalLogic = store.builderConfig.conditionalLogic.filter((item) => {
    item.conditions = item?.conditions?.filter(
      (condition) =>
        condition.selectedField !== store.elements[deleteIndex.value].hiddenFieldQueryKey
    );
    return item.conditions?.length > 0;
  });
  store.elements.splice(deleteIndex.value, 1);
  showDeleteModal.value = false;

  // Clean up contact associations if needed
  store.cleanupContactAssociations();
};

const cancelCallbackDeleteUpdate = () => {
  showDeleteModal.value = false;
};

const cloneCustomField = async (index: number, slideIndex?: number) => {
  const isForm = !!store.formId;
  const isOneQuestionSurvey = !!store.surveyId && !!store.formAction.fieldSettingEnable;
  const isQuiz = !!store.quizId;
  const baseList = isForm ? store.elements : store.slides[slideIndex].slideData;
  const source = baseList[index];
  // Guard: do not clone non-contact models
  if (source?.model && source.model !== "contact") {
    return;
  }
  const { payload, uniqueKeyPart, name: newName } = buildClonedCustomFieldPayload(source, store);

  // Local insert for immediate editing/UI; persistence deferred until Save
  const tempHiddenKey = (payload.fieldKey || "").includes(".")
    ? (payload.fieldKey as string).split(".")[1]
    : (payload.fieldKey as string);
  const tempClone: any = {
    ...source,
    label: newName,
    fieldKey: payload.fieldKey,
    hiddenFieldQueryKey: tempHiddenKey,
    custom: true,
    // Allow editing advanced settings (name/unique key) before Save
    customEdited: false,
    customfieldUpdated: false,
    __pendingClone: true,
  };
  // Give the clone a unique temporary tag in the builder to avoid
  // any state collisions keyed by `tag` before the next save.
  (tempClone as any).tag = tempHiddenKey || uniqueKeyPart;
  // Clear ids so Save treats this as a new custom field (create, not update)
  delete (tempClone as any).Id;
  delete (tempClone as any).id;
  // Ensure sidebar binds to editable label for pending clones
  delete (tempClone as any).customFieldLabel;
  delete (tempClone as any).name;
  // Ensure a fresh uuid for quizzes so original and clone never share the same elementId
  if (!isForm && store.quizId) {
    tempClone.uuid = uuidv4();
  }
  // Determine target list and position based on layout
  let targetSlideIndex = slideIndex;
  let targetList = baseList;
  let insertPos = index + 1;

  if (!isForm && isOneQuestionSurvey && typeof slideIndex === "number") {
    // Create a new slide after current and place the clone at position 0
    const newSlideIndex = slideIndex + 1;
    const newSlide: any = {
      slideName: t("survey.slideLabel", { n: store.slides.length + 1 }),
      slideData: [],
      id: new Date().getTime() + "-" + store.slides.length,
    };
    store.slides.splice(newSlideIndex, 0, newSlide);
    store.formAction.fieldPerPage = store.slides.length;
    targetSlideIndex = newSlideIndex;
    targetList = store.slides[newSlideIndex].slideData;
    insertPos = 0;
  }

  // Quiz: if cloning a question (JUMP_TO_FIELDS) and page already has a question, put clone on a new page
  if (!isForm && isQuiz && typeof slideIndex === "number") {
    const isQuestion = JUMP_TO_FIELDS.includes((source as any)?.dataType);
    const hasQuestionOnSlide = baseList.some((el: any) => JUMP_TO_FIELDS.includes(el?.dataType));
    if (isQuestion && hasQuestionOnSlide) {
      const newSlideIndex = slideIndex + 1;
      const newSlide: any = {
        slideName: t("quiz.pageLabel", { n: store.slides.length + 1 }),
        slideData: [],
        id: uuidv4(),
      };
      store.slides.splice(newSlideIndex, 0, newSlide);
      store.formAction.fieldPerPage = store.slides.length;
      targetSlideIndex = newSlideIndex;
      targetList = store.slides[newSlideIndex].slideData;
      insertPos = 0;
      // Scroll by current slide's height to bring the new slide into view
      scrollContainerBySlideHeight(
        "form-builder",
        "form-builder-container",
        ".survey-form-container",
        slideIndex,
        20
      );
    }
  }

  targetList.splice(insertPos, 0, tempClone);
  store.anyUnsavedChanges = true;
  store.addToHistory();

  // Activate the newly inserted clone
  if (isForm) {
    store.currentCustomSidebarPreview = insertPos;
    store.isAnyElementActive = true;
  } else if (store.quizId) {
    const slideIdToFocus = targetSlideIndex ?? slideIndex;
    const slideObj = store.slides[slideIdToFocus];
    store.currentActiveIndex = [slideObj.id, tempClone.uuid];
    store.currentSlideIndex = slideObj.id;
    store.sidebarOverlaySettings = {
      elementId: tempClone.uuid,
      slideId: slideObj.id,
      formIndex: undefined,
    } as any;
    store.isAnyElementActive = true;
  } else if (store.surveyId) {
    store.currentActiveIndex = [targetSlideIndex, insertPos];
    store.currentSlideIndex = targetSlideIndex;
    store.sidebarOverlaySettings = {
      index: insertPos,
      slideIndex: targetSlideIndex,
      formIndex: undefined,
    } as any;
    store.isAnyElementActive = true;
  }
};

const removeField = (tag: string, index: number, slideIndex?: number) => {
  store.anyUnsavedChanges = true;
  let opportunityCustomFields = [];

  // Get the element being removed
  const elementToRemove =
    store.surveyId || store.quizId
      ? store.slides[slideIndex].slideData[index]
      : store.elements[index];

  // Check if it's a primary field
  const primaryCheck = isPrimaryField(elementToRemove, store.customObjects || []);
  if (primaryCheck.isPrimary) {
    // If this is the only custom object field of this type, remove it directly
    if (
      isOnlyCustomObjectField(
        elementToRemove,
        store.surveyId || store.quizId ? null : store.elements,
        store.surveyId || store.quizId ? store.slides : null
      )
    ) {
      // Proceed with direct removal
      if (store.surveyId || store.quizId) {
        if (slideIndex !== undefined) {
          store.slides[slideIndex].slideData.splice(index, 1);
          store.isAnyElementActive = false;
          store.currentActiveIndex = [undefined, undefined];
        }
      } else {
        store.elements.splice(index, 1);
        if (store.currentCustomSidebarPreview === index) {
          store.currentCustomSidebarPreview = undefined;
        } else if (
          typeof store.currentCustomSidebarPreview === "number" &&
          store.currentCustomSidebarPreview > index
        ) {
          store.currentCustomSidebarPreview--;
        }
      }

      // Clear association settings for this object model
      if (store.contactAssociationSettings?.objectModel === elementToRemove.model) {
        store.contactAssociationSettings = null;
      }
      store.currentCustomObjectModel = null;

      store.anyUnsavedChanges = true;
      store.addToHistory();
      store.cleanupContactAssociations();
      return;
    }

    // Show primary field removal confirmation modal only if there are other fields
    primaryFieldInfo.value = {
      fieldName: primaryCheck.fieldName,
      objectName: primaryCheck.objectName,
      objectSingular: primaryCheck.objectSingular,
    };
    showRemovePrimaryFieldModal.value = true;
    deleteIndex.value = index;
    deleteSlideIndex.value = slideIndex;
    return; // Stop execution here, let the modal handle the action
  }

  // Continue with normal removal logic for non-primary fields
  if (store.surveyId || store.quizId) {
    if (slideIndex !== undefined) {
      store.slides[slideIndex].slideData.splice(index, 1);
      store.isAnyElementActive = false;
      store.currentActiveIndex = [undefined, undefined];
      opportunityCustomFields = store.slides.reduce((acc, slide) => {
        const filteredFields = slide.slideData.filter((field) => field.model === "opportunity");
        return acc.concat(filteredFields);
      }, []);
    }
  } else {
    if (
      store.builderConfig.conditionalLogic?.some((item) =>
        item.conditions?.some(
          (condition) => condition.selectedField === store.elements[index].hiddenFieldQueryKey
        )
      )
    ) {
      showDeleteModal.value = true;
      deleteIndex.value = index;
    } else {
      store.elements.splice(index, 1);
      if (store.currentCustomSidebarPreview === index) {
        store.currentCustomSidebarPreview = undefined;
      } else if (
        typeof store.currentCustomSidebarPreview === "number" &&
        store.currentCustomSidebarPreview > index
      ) {
        store.currentCustomSidebarPreview--;
      }
    }
    opportunityCustomFields = store.elements.filter((field) => field.model === "opportunity");
  }
  if (opportunityCustomFields?.length === 0) {
    store.opportunitySettings = null;
  } else {
    store.opportunitySettings.payload.customFields = opportunityCustomFields.map(
      ({ Id, fieldKey }) => ({
        id: Id,
        key: fieldKey.replace("opportunity.", ""),
      })
    );
  }
  store.addToHistory();

  // Clean up contact associations if needed
  store.cleanupContactAssociations();
};

/**
 * Handle primary field modal cancel
 */
const handlePrimaryFieldCancel = () => {
  showRemovePrimaryFieldModal.value = false;
  primaryFieldInfo.value = { fieldName: "", objectName: "", objectSingular: "" };
};

/**
 * Handle remove all custom object fields
 */
const handleRemoveAllCustomObjectFieldsLocal = () => {
  const elementToRemove =
    store.surveyId || store.quizId
      ? store.slides[deleteSlideIndex.value as number]?.slideData?.[deleteIndex.value]
      : store.elements[deleteIndex.value];

  // Use the utility function
  handleRemoveAllCustomObjectFields(
    elementToRemove,
    store.surveyId || store.quizId ? null : store.elements,
    store.surveyId || store.quizId ? store.slides : null,
    store,
    deleteIndex.value
  );

  showRemovePrimaryFieldModal.value = false;
  primaryFieldInfo.value = { fieldName: "", objectName: "", objectSingular: "" };
  deleteSlideIndex.value = undefined;
};

const isActiveElement = (slideIndex, elementIndex, elementId, slideId) => {
  return isBuilderElementActive(store, {
    elementIndex,
    slideIndex,
    elementId,
    slideId,
  });
};

const labelAlignment = () => {
  if (currentLayout.value === CONSTANTS.ONE_COLUMN && store.deviceMode !== "mobile") {
    const elementAlignment =
      !props.element.labelAlignment || props.element.labelAlignment === "default"
        ? store.fieldStyle.labelAlignment
        : props.element.labelAlignment;
    const validAlign = ["left", "right"].includes(elementAlignment);
    return {
      width: validAlign ? store.fieldStyle.labelWidth + "px" : "",
      display: validAlign ? "inline-block" : "block",
      cssFloat: validAlign ? "left" : "none",
      textAlign: validAlign ? elementAlignment : "",
      padding: validAlign ? "0 10px 0 0" : "",
    };
  }
  return {};
};

const getInputCSSList = (): string[] => {
  return store.builderConfig.inputStyleType === "line" ? ["line-form-style"] : [];
};

const getStyle = () => {
  return {
    fontSize: store.fieldStyle.placeholderFontSize + "px",
    width: "100%",
  };
};
const getEmailPhoneValidateClass = (fieldWidthPercentage: number) => {
  return [CONSTANTS.TWO_COLUMN, CONSTANTS.SINGLE_LINE].includes(currentLayout.value) &&
    fieldWidthPercentage <= 50
    ? "block space-y-3"
    : "flex";
};

const updatePicklistOptions = (newOptions: Array<string | any>, element: any) => {
  if (store.formId) {
    // For forms
    const elementIndex = store.elements.findIndex((el) => el === element);
    if (elementIndex !== -1) {
      if (element.enableCalculations) {
        // Store the complex objects in calculatedOptions
        store.elements[elementIndex].calculatedOptions = [...newOptions];
        // For backward compatibility, maintain picklistOptions as an array of strings
        store.elements[elementIndex].picklistOptions = newOptions.map((option) =>
          typeof option === "object" && option !== null ? option.label : option
        );
      } else {
        store.elements[elementIndex].picklistOptions = [...newOptions];
      }

      if (store.elements[elementIndex].id) {
        // on custom field update, set the customfieldUpdated flag to true to make custom field update API call
        store.elements[elementIndex].customfieldUpdated = true;
      }
      store.anyUnsavedChanges = true;
      store.addToHistory();
    }
  } else if (store.surveyId || store.quizId) {
    // For surveys and quizzes
    const slideIndex = store.slides.findIndex((slide) =>
      slide.slideData.some((el) => el === element)
    );
    if (slideIndex !== -1) {
      const elementIndex = store.slides[slideIndex].slideData.findIndex((el) => el === element);
      if (elementIndex !== -1) {
        if (element.enableCalculations) {
          // Store the complex objects in calculatedOptions
          store.slides[slideIndex].slideData[elementIndex].calculatedOptions = [...newOptions];
          // For backward compatibility, maintain picklistOptions as an array of strings
          store.slides[slideIndex].slideData[elementIndex].picklistOptions = newOptions.map(
            (option) => (typeof option === "object" && option !== null ? option.label : option)
          );
        } else {
          store.slides[slideIndex].slideData[elementIndex].picklistOptions = [...newOptions];
        }

        if (store.slides[slideIndex].slideData[elementIndex]?.id) {
          // on custom field update, set the customfieldUpdated flag to true to make custom field update API call
          store.slides[slideIndex].slideData[elementIndex].customfieldUpdated = true;
        }
        store.anyUnsavedChanges = true;
        store.addToHistory();
      }
    }
  }
};

const handleInputClick = (event: MouseEvent) => {
  const target = event.target as HTMLElement;
  if (!inputClicked.value) {
    // Select all text in the contenteditable element (works for input and div)
    if (target instanceof HTMLInputElement || (target as any).select) {
      // For real input fields
      (target as HTMLInputElement).select();
    } else {
      const range = document.createRange();
      range.selectNodeContents(target);
      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
    inputClicked.value = true;
  }
  // Subsequent clicks will let browser place the caret normally
};

const rawInputLabelHandler = (e: Event) => {
  const target = e.target as HTMLElement;
  const caret = getCaretOffsetWithin(target);
  const text = (target.textContent || "").replace(/\u200B/g, "");

  localLabel.value = text;
  onLabelInput();
  handleSafariCursor(e);
  nextTick(() => setCaretOffsetWithin(target, caret));
};

const handleLabelInput = withIMECheck(rawInputLabelHandler);
const compositionEndHandler = handleCompositionEnd(rawInputLabelHandler);

const handlePlaceholderInput = (event: Event) => {
  const target = event.target as HTMLInputElement;
  localPlaceholder.value = target.value;
  emit("update:placeholder", localPlaceholder.value);
};

const rawInputShortLabelHandler = (e: Event) => {
  handleShortLabelInputUtil(e, localShortLabel, updateLocalShortLabel);
};

const handleShortLabelInput = withIMECheck(rawInputShortLabelHandler);
const compositionEndHandlerShortLabel = handleCompositionEnd(rawInputShortLabelHandler);
// Helper to relax typing for RatingField prop
const toAny = (v: unknown): any => v as any;

const openSettings = (index: number, slideIndex?: number) => {
  // Forms
  if (store.formId) {
    store.currentCustomSidebarPreview = index;
    store.isAnyElementActive = true;
    return;
  }
  // Quizzes
  if (store.quizId && props.slide && props.element?.uuid) {
    store.currentActiveIndex = [props.slide.id, props.element.uuid];
    store.currentSlideIndex = slideIndex;
    store.sidebarOverlaySettings = {
      elementId: props.element.uuid,
      slideId: props.slide.id,
      formIndex: undefined,
    };
    store.isAnyElementActive = true;
    return;
  }
  // Surveys
  if (store.surveyId) {
    store.currentActiveIndex = [slideIndex, index];
    store.currentSlideIndex = slideIndex;
    store.sidebarOverlaySettings = { index, slideIndex, formIndex: undefined };
    store.isAnyElementActive = true;
  }
};
</script>

<template>
  <div class="menu-item">
    <div
      class="form-builder--item"
      :data-active="isActiveElement(slideIndex, index, element.uuid, slide.id) ? 'true' : 'false'"
    >
      <label
        v-if="!isForm && element?.description !== undefined"
        for="label-input"
        style="display: block; overflow-wrap: break-word"
        v-html="element.description"
      ></label>
      <div style="display: inline-flex" :style="labelAlignment()">
        <div v-show="formLabelVisible" class="label-container">
          <div
            ref="inputElement"
            :key="resetKey"
            class="label-input font-inter cursor-text border-0 bg-transparent p-0 text-sm shadow-none focus:border-gray-300 focus:ring-0"
            :data-field-type="element.dataType"
            :data-placeholder="t('common.labelPlaceholder')"
            contenteditable="true"
            @blur="onLabelBlur"
            @input="handleLabelInput"
            @compositionstart="handleCompositionStart"
            @compositionend="compositionEndHandler"
            @keydown="(e) => { if (!handleLabelTabNavigation(e as KeyboardEvent)) handleSafariCursor(e); }"
            @click="handleInputClick"
            v-text="localLabel"
          ></div>
          <span v-if="element.required" class="required-asterisk">*</span>
        </div>
        <div v-if="element.hidden">
          <small class="ml-[5px] rounded-sm bg-[#e1e1e1] px-[5px] py-[2px]">
            {{ t("common.hidden") }}
          </small>
        </div>
      </div>
      <TextField
        v-if="
          element.dataType === 'TEXT' ||
          element.dataType === 'SCORE' ||
          element.dataType === 'STANDARD_FIELD' ||
          element.dataType === 'EMAIL'
        "
        :element="element"
        :placeholder="element.placeholder"
        :class-list="getInputCSSList()"
        :style-object="getStyle()"
        :readonly="true"
        :is-active="isActiveElement(slideIndex, index, element.uuid, slide.id)"
        @update:placeholder="onUpdatePlaceholder"
        @update:short-label="onUpdateShortLabel"
      />
      <LargeTextField
        v-if="element.dataType === 'LARGE_TEXT'"
        :element="element"
        :placeholder="element.placeholder"
        :class-list="getInputCSSList()"
        :style-object="getStyle()"
        :readonly="true"
        :is-active="isActiveElement(slideIndex, index, element.uuid, slide.id)"
        @update:placeholder="onUpdatePlaceholder"
        @update:short-label="onUpdateShortLabel"
      />
      <TextboxListField
        v-if="element.dataType === 'TEXTBOX_LIST'"
        :placeholder="element.placeholder"
        :picklist-options="element.picklistOptions"
        :class-list="getInputCSSList()"
        :style-object="getStyle()"
        :is-active="isActiveElement(slideIndex, index, element.uuid, slide.id)"
        :readonly="true"
        :element="element"
        :custom-field-created="element.customEdited || !element.custom"
        @update:short-label="onUpdateShortLabel"
        @update:picklistOptions="updatePicklistOptions($event, element)"
      />
      <CheckboxField
        v-if="element.dataType === 'CHECKBOX'"
        :placeholder="element.placeholder"
        :picklist-options="
          element.enableCalculations ? element.calculatedOptions : element.picklistOptions
        "
        :layout="store?.builderConfig?.layout"
        :style-object="getStyle()"
        :is-active="isActiveElement(slideIndex, index, element.uuid, slide.id)"
        :readonly="true"
        :field="element"
        :custom-field-created="element.customEdited || !element.custom"
        @update:short-label="onUpdateShortLabel"
        @update:picklistOptions="updatePicklistOptions($event, element)"
      />
      <RadioField
        v-if="element.dataType === 'RADIO'"
        :field="element"
        :placeholder="element.placeholder"
        :picklist-options="
          element.enableCalculations ? element.calculatedOptions : element.picklistOptions
        "
        :picklist-options-image="element.picklistOptionsImage"
        :style-object="getStyle()"
        :is-active="isActiveElement(slideIndex, index, element.uuid, slide.id)"
        :layout="store?.builderConfig?.layout"
        :disabled="true"
        :custom-field-created="element.customEdited || !element.custom"
        @update:short-label="onUpdateShortLabel"
        @update:picklistOptions="updatePicklistOptions($event, element)"
      />
      <NumericalField
        v-if="element.dataType === 'NUMERICAL' && element.type !== 'rating'"
        :element="element"
        :placeholder="element.placeholder"
        :class-list="getInputCSSList()"
        :style-object="getStyle()"
        :readonly="true"
        :is-active="isActiveElement(slideIndex, index, element.uuid, slide.id)"
        @update:placeholder="onUpdatePlaceholder"
        @update:short-label="onUpdateShortLabel"
      />
      <div v-if="element.dataType === 'PHONE'" class="flex flex-col">
        <div
          class="gap-x-3"
          :class="[getEmailPhoneValidateClass(element.fieldWidthPercentage)]"
          :style="getStyle()"
        >
          <input
            v-model="localPlaceholder"
            :placeholder="localPlaceholder"
            class="form-placeholder form-control shadow-none focus:outline-transparent focus:ring-0"
            :class="getInputCSSList()"
            :style="{ fontSize: store.fieldStyle.placeholderFontSize + 'px' }"
            @input="handlePlaceholderInput"
          />
          <UIButton
            v-if="element?.enableValidatePhone && !element.hidden"
            id="verify-phone"
            class="pointer-events-none h-auto"
            :style="{ fontSize: store.fieldStyle.placeholderFontSize + 'px' }"
            >{{ t("common.verifyPhone") }}</UIButton
          >
        </div>
        <template v-if="isActiveElement(slideIndex, index, element.uuid, slide.id)">
          <div
            ref="shortLabelElement"
            :key="resetKey"
            contenteditable="true"
            class="short-label font-inter cursor-text border-0 bg-transparent p-0 text-sm shadow-none focus:outline-none"
            :data-placeholder="t('common.shortLabelPlaceholder')"
            @blur="updateLocalShortLabel"
            @input="handleShortLabelInput"
            @compositionstart="handleCompositionStart"
            @compositionend="compositionEndHandlerShortLabel"
            @click="(e) => e.stopPropagation()"
            @focus="(e) => e.stopPropagation()"
            v-text="localShortLabel"
          ></div>
        </template>
        <template v-else>
          <span v-if="element.shortLabel" class="short-label font-inter break-words text-sm">
            {{ element.shortLabel }}
          </span>
        </template>
      </div>

      <MonetaryField
        v-if="element.dataType === 'MONETORY'"
        :element="element"
        :placeholder="element.placeholder"
        :class-list="getInputCSSList()"
        :style-object="getStyle()"
        :style-input="{ fontSize: store.fieldStyle.placeholderFontSize + 'px' }"
        :readonly="true"
        :is-active="isActiveElement(slideIndex, index, element.uuid, slide.id)"
        @update:placeholder="onUpdatePlaceholder"
        @update:short-label="onUpdateShortLabel"
      />
      <SingleOptionsField
        v-if="element.dataType === 'SINGLE_OPTIONS'"
        :element="element"
        :placeholder="element.placeholder"
        :class-list="getInputCSSList()"
        :style-object="getStyle()"
        :picklist-options="
          element.enableCalculations ? element.calculatedOptions : element.picklistOptions
        "
        :is-active="isActiveElement(slideIndex, index, element.uuid, slide.id)"
        :custom-field-created="element.customEdited || !element.custom"
        @update:placeholder="onUpdatePlaceholder"
        @update:short-label="onUpdateShortLabel"
        @update:picklist-options="updatePicklistOptions($event, element)"
      />
      <SingleOptionsField
        v-if="element.dataType === 'MULTIPLE_OPTIONS'"
        :element="element"
        :placeholder="element.placeholder"
        :class-list="getInputCSSList()"
        :style-object="getStyle()"
        :picklist-options="
          element.enableCalculations ? element.calculatedOptions : element.picklistOptions
        "
        :is-active="isActiveElement(slideIndex, index, element.uuid, slide.id)"
        :custom-field-created="element.customEdited || !element.custom"
        @update:placeholder="onUpdatePlaceholder"
        @update:short-label="onUpdateShortLabel"
        @update:picklist-options="updatePicklistOptions($event, element)"
      />
      <TextField
        v-if="element.dataType === 'DATE'"
        :element="element"
        :placeholder="element.placeholder"
        :class-list="getInputCSSList()"
        :style-object="getStyle()"
        :readonly="true"
        :is-active="isActiveElement(slideIndex, index, element.uuid, slide.id)"
        @update:placeholder="onUpdatePlaceholder"
        @update:short-label="onUpdateShortLabel"
      />
      <FileUploadField
        v-if="element.dataType === 'FILE_UPLOAD'"
        :placeholder="element.placeholder"
        :picklist-options="element.picklistOptions"
        :field="element"
        :is-active="isActiveElement(slideIndex, index, element.uuid, slide.id)"
        @update:placeholder="onUpdatePlaceholder"
        @update:short-label="onUpdateShortLabel"
      />
      <SignatureField
        v-if="element.dataType === 'SIGNATURE'"
        :element="element"
        :style-object="getStyle()"
        :is-active="isActiveElement(slideIndex, index, element.uuid, slide.id)"
        @update:short-label="onUpdateShortLabel"
      />
      <RatingField
        v-if="element.dataType === 'RATING'"
        :element="toAny(element)"
        :placeholder="element.placeholder"
        :class-list="getInputCSSList()"
        :style-object="getStyle()"
        :is-active="isActiveElement(slideIndex, index, element.uuid, slide.id)"
        @update:short-label="onUpdateShortLabel"
        @update:lowest-rating="onUpdateLowestRating"
        @update:highest-rating="onUpdateHighestRating"
      />
    </div>
    <div
      v-if="isActiveElement(slideIndex, index, element.uuid, slide.id)"
      class="outline-border"
    ></div>
    <div v-if="isActiveElement(slideIndex, index, element.uuid, slide.id)" class="builder-actions">
      <a
        class="builder-action"
        :aria-label="t('common.openSettings')"
        @mousedown.stop="openSettings(index, slideIndex)"
      >
        <Settings02Icon class="h-4 w-4"></Settings02Icon>
      </a>
      <a
        v-if="!element?.model || element?.model === 'contact'"
        class="builder-action"
        :aria-label="t('common.cloneField')"
        @mousedown.stop="cloneCustomField(index, slideIndex)"
      >
        <Copy05Icon class="h-4 w-4"></Copy05Icon>
      </a>
      <a
        class="builder-action"
        :aria-label="t('common.removeField')"
        @mousedown.stop="removeField(element.tag, index, slideIndex)"
      >
        <Trash01Icon class="h-4 w-4"></Trash01Icon>
      </a>
    </div>
    <div
      v-if="isActiveElement(slideIndex, index, element.uuid, slide.id) && !store.leftSidebarOpen"
      class="add-new-element"
      @mousedown.stop="() => emitter.emit('open-left-side-bar')"
    >
      <PlusIcon class="h-4 w-4"></PlusIcon>
    </div>
  </div>
  <AlertModal
    v-if="showDeleteModal"
    :description="t('form.conditonalLogic.deleteAlertMessage')"
    :title="t('form.conditonalLogic.deleteField')"
    :positive-text="t('common.delete')"
    :negative-text="t('common.cancel')"
    :show-modal-popup="showDeleteModal"
    header-type="error"
    footer-type="error"
    @pop:change="cancelCallbackDeleteUpdate"
    @pop:positive="successCallbackDeleteUpdate"
    @pop:negative="cancelCallbackDeleteUpdate"
  />
  <RemovePrimaryFieldModal
    v-if="showRemovePrimaryFieldModal"
    :show="showRemovePrimaryFieldModal"
    :field-name="primaryFieldInfo.fieldName"
    :object-name="primaryFieldInfo.objectName"
    :object-singular="primaryFieldInfo.objectSingular"
    @update:cancel="handlePrimaryFieldCancel"
    @update:removeAll="handleRemoveAllCustomObjectFieldsLocal"
  />
</template>

<style scoped>
.form-control {
  border-radius: 0.3125rem;
  padding: 15px 20px;
  font-size: 0.875rem;
}
.form-control:disabled,
.form-control [readonly] {
  background-color: #e9ecef !important;
  opacity: 1;
}
.vs__search,
.vs__search:focus {
  padding: 12px 18px !important;
}
.form-builder--item .v-select {
  width: 100%;
  background: #f3f8fb;
  border-radius: 4px !important;
  height: auto;
  border: transparent;
}
.form-builder--item .v-select .vs__dropdown-toggle {
  height: 50px;
}
.add-custom-opt {
  padding: 3px;
  font-size: 14px;
}

.close-icon {
  position: absolute;
  right: -7px;
  top: -10px;
  opacity: 1;
  -webkit-transition: all 0.5s ease-in;
  transition: all 0.5s ease-in;
  font-size: 10px;
  background: #f5f5f5;
  color: #2a3135;
  border-radius: 50%;
  height: 20px;
  cursor: pointer;
}
.form-builder--item {
  border-radius: 4px;
  border: 1px solid transparent;
  -webkit-transition: border 0.2s ease-in-out;
  -o-transition: border 0.2s ease-in-out;
  transition: border 0.2s ease-in-out;
  position: relative;
}
/* .form-builder--item:hover {
  border: 2px dashed rgba(175, 184, 188, 0.5);
}
.form-builder--item:hover .form-builder--actions {
  opacity: 1;
  visibility: visible;
} */
.form-builder--item h1,
.form-builder--item h2,
.form-builder--item h3,
.form-builder--item h4,
.form-builder--item h5,
.form-builder--item h6 {
  margin-bottom: 0;
}
.form-builder--item label {
  font-size: 0.875rem;
  color: #2a3135;
  margin-bottom: 0.5;
}
.label-container {
  display: inline;
  width: auto;
  overflow: visible;
  margin-bottom: 6px;
  position: relative;
  max-width: 100%;
}

.label-input {
  display: inline;
  min-width: 0;
  max-width: 100%;
  outline: none;
  white-space: normal;
  overflow: visible;
  text-overflow: clip;
  margin-bottom: 2px;
  word-break: break-word;
  min-height: 24px;
}

.label-input[data-field-type="DATE"],
.label-input[data-field-type="SINGLE_OPTIONS"],
.label-input[data-field-type="MULTIPLE_OPTIONS"] {
  min-width: 100px;
  max-width: none;
}

.label-input:focus {
  background-color: rgba(200, 200, 200, 0.1);
  border-radius: 2px;
  outline: none;
}

.required-asterisk {
  font-weight: bold;
  padding-left: 0;
  font-size: 14px;
  display: inline;
  margin-left: 4px;
}

.short-label:empty:before {
  content: attr(data-placeholder);
}

.builder-actions {
  position: absolute;
  right: -8px;
  top: -8px;
  display: flex;
  gap: 0;
  background: #155eef;
  border-radius: 0 8px 0 8px;
  padding: 0 8px;
  padding-bottom: 2px;
  z-index: 1;
}
.builder-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 25px;
  height: 25px;
  color: #ffffff;
  cursor: pointer;
}
.builder-action:hover svg {
  transform: scale(1.15);
  transition: transform 0.25s ease;
}

.menu-item {
  position: relative;
}

.outline-border {
  position: absolute;
  top: -8px;
  left: -8px;
  right: -8px;
  bottom: -8px;
  border: 2px solid #155eef;
  border-radius: 8px;
  pointer-events: none;
  z-index: 1;
}

.add-new-element {
  position: absolute;
  left: 50%;
  bottom: -18px;
  transform: translateX(-50%);
  width: 24px;
  height: 24px;
  background-color: #155eef;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  cursor: pointer;
  z-index: 2;
}

/* Extend active background to outline border to avoid white gap */
.menu-item.active::before,
.form-builder--item[data-active="true"]::before {
  content: "";
  position: absolute;
  top: -8px;
  right: -8px;
  bottom: -8px;
  left: -8px;
  background: rgba(0, 78, 235, 0.05);
  border-radius: 8px;
  pointer-events: none;
}
</style>
